import React, { useEffect, useRef, useState } from "react";
import { HelpCircle, Send, X } from "lucide-react";
import { api } from "./api";
import TexteAssistant from "../components/TexteAssistant";

/* L'assistant INTERNE de la console : « comment je fais pour… ». Réservé aux comptes de la
   console (il passe par /api/console, donc par la connexion de l'école) et séparé de
   l'assistant du site : il connaît le guide et le manuel de la console, pas les données.
   La conversation suit la session de l'onglet du navigateur. */

const CLE = "aide_console";
const SUGGESTIONS = [
  "Comment générer et envoyer les factures du semestre ?",
  "Comment ajouter un nouveau cours au planning ?",
  "Comment valider le décompte d'un prof ?",
  "Une famille n'arrive pas à se connecter à l'app, je fais quoi ?",
];

const lire = () => {
  try { return JSON.parse(sessionStorage.getItem(CLE)) || { id: "", messages: [] }; }
  catch { return { id: "", messages: [] }; }
};

export default function AideConsole({ ecran }) {
  const [ouvert, setOuvert] = useState(false);
  const [etat, setEtat] = useState(lire);
  const [question, setQuestion] = useState("");
  const [enCours, setEnCours] = useState(false);
  const fond = useRef(null);
  const champ = useRef(null);

  useEffect(() => { try { sessionStorage.setItem(CLE, JSON.stringify(etat)); } catch { /* privé */ } }, [etat]);
  useEffect(() => { fond.current?.scrollIntoView({ block: "end" }); }, [etat.messages, ouvert, enCours]);
  useEffect(() => {
    if (!ouvert) return undefined;
    champ.current?.focus();
    const echap = (e) => { if (e.key === "Escape") setOuvert(false); };
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [ouvert]);

  const envoyer = async (texte) => {
    const q = (typeof texte === "string" ? texte : question).trim();
    if (!q || enCours) return;
    setQuestion("");
    setEnCours(true);
    const avant = etat;
    setEtat({ ...avant, messages: [...avant.messages, { role: "user", texte: q }] });
    try {
      const r = await api("/assistant-interne/message", {
        method: "POST", body: JSON.stringify({ message: q, conversation_id: avant.id, ecran }),
      });
      setEtat({ id: r.conversation_id, messages: [...avant.messages, { role: "user", texte: q },
        { role: "assistant", texte: r.reponse }] });
    } catch (e) {
      setEtat({ ...avant, messages: [...avant.messages, { role: "user", texte: q },
        { role: "assistant", texte: e.message, erreur: true }] });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="fixed z-[55] right-4 bottom-4 md:right-6 md:bottom-6 flex flex-col items-end gap-3 print:hidden"
      data-testid="aide-console">
      {ouvert && (
        <div className="bg-white rounded-3xl shadow-2xl border flex flex-col overflow-hidden"
          style={{ borderColor: "var(--border)", width: "min(430px, calc(100vw - 2rem))", height: "min(640px, calc(100dvh - 7rem))" }}
          role="dialog" aria-label="Aide de la console">
          <div className="flex items-center gap-3 px-5 py-3.5" style={{ background: "#0F1B2D", color: "#fff" }}>
            <HelpCircle size={22} style={{ color: "#7DE8ED" }} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Aide de la console</div>
              <div className="text-[11px]" style={{ color: "rgba(255,255,255,.65)" }}>
                Comment faire ceci ou cela · réservé à l'équipe
              </div>
            </div>
            {etat.messages.length > 0 && (
              <button className="text-[11px] underline mr-1" style={{ color: "rgba(255,255,255,.75)" }}
                onClick={() => setEtat({ id: "", messages: [] })}>Nouvelle</button>
            )}
            <button onClick={() => setOuvert(false)} aria-label="Fermer"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm" style={{ background: "#F8FAFC" }}>
            <div className="rounded-2xl px-4 py-2.5 bg-white border max-w-[90%]" style={{ borderColor: "var(--border)" }}>
              Bonjour ! Pose-moi une question sur l'utilisation de la console ou des applications.
              Je n'ai pas accès aux données : je t'explique où cliquer.
            </div>
            {etat.messages.length === 0 && (
              <div className="flex flex-col items-start gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => envoyer(s)} className="text-left text-xs px-3 py-1.5 rounded-full border bg-white"
                    style={{ borderColor: "var(--primary)", color: "#007E85" }}>{s}</button>
                ))}
              </div>
            )}
            {etat.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`rounded-2xl px-4 py-2.5 max-w-[90%]${m.role === "user" ? " whitespace-pre-wrap" : " border"}`}
                  style={m.role === "user" ? { background: "var(--primary)", color: "#04202A" }
                    : { background: m.erreur ? "#FEF2F2" : "#fff", borderColor: m.erreur ? "#FECACA" : "var(--border)" }}>
                  {m.role === "user" ? m.texte : <TexteAssistant texte={m.texte} nouvelOnglet />}
                </div>
              </div>
            ))}
            {enCours && <p className="text-xs text-[var(--muted)]">L'assistant cherche dans le guide…</p>}
            <div ref={fond} />
          </div>
          <div className="border-t px-3 py-3 flex gap-2 bg-white" style={{ borderColor: "var(--border)" }}>
            <input ref={champ} className="input-field flex-1" value={question} maxLength={2000}
              placeholder="Ex. : comment je relance un impayé ?" onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && envoyer()} data-testid="aide-console-question" />
            <button onClick={() => envoyer()} disabled={enCours || !question.trim()} aria-label="Envoyer"
              className="rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-40 shrink-0"
              style={{ background: "var(--primary)", color: "#04202A" }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
      {!ouvert && (
        <button onClick={() => setOuvert(true)} aria-label="Aide de la console" data-testid="aide-console-ouvrir"
          className="rounded-full h-12 pl-4 pr-5 shadow-xl flex items-center gap-2 text-sm font-semibold transition-transform hover:-translate-y-0.5"
          style={{ background: "#0F1B2D", color: "#fff" }}>
          <HelpCircle size={20} style={{ color: "#7DE8ED" }} /> Aide
        </button>
      )}
    </div>
  );
}
